// Éléments partagés des accueils (charte D-038, « exceptions d'abord ») : situation du référentiel,
// organisation de l'utilisateur, points à traiter tirés des données réelles, qualité de l'information.
// Aucune donnée inventée : ce qui n'existe pas encore est « indisponible » avec son motif.

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api, ErreurApi, messageErreur } from '../../api/client';
import type { Organisation, Situation } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { useAPropos } from '../../api/requetes';
import { Indisponible, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { ListeAlertesCompacte } from '../stocks/composants';
import type { AlerteResume } from '../stocks/types';
import { jourLong } from '../../composants/format';

export function useSituation() {
  return useQuery({ queryKey: ['accueil-situation'], queryFn: async () => (await api<Situation>('/api/v1/accueil/situation')).donnees });
}

/** Organisation de la première affectation, si elle est lisible avec les habilitations de l'utilisateur. */
export function useOrganisationCourante() {
  const { etat } = useSession();
  const id = etat?.affectations?.[0]?.organisationId;
  return useQuery({
    queryKey: ['organisation', id],
    queryFn: async () => (await api<Organisation>(`/api/v1/organisations/${id}`)).donnees,
    enabled: Boolean(id),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface Doublon {
  a: { id: string; nom: string; categorie: string; etatValidation: string };
  b: { id: string; nom: string; categorie: string; etatValidation: string };
  distanceMetres: number;
}

export function useDoublons(actif = true) {
  return useQuery({
    queryKey: ['doublons'],
    queryFn: async () => api<Doublon[]>('/api/v1/installations/doublons'),
    enabled: actif,
    retry: false,
  });
}

/** Ligne de contexte discrète au-dessus du titre : organisation, jour, mention de présentation. */
export function useLigneContexte(): string {
  const organisation = useOrganisationCourante();
  const aPropos = useAPropos();
  return [organisation.data?.nom, jourLong(), aPropos.data?.environnement.mention ? 'présentation, données fictives' : null].filter(Boolean).join(', ');
}

export const pluriel = (n: number, singulier: string, pluriel = `${singulier}s`) => `${formaterNombre(n)} ${n > 1 ? pluriel : singulier}`;

export interface PointAttention {
  cle: string;
  ton: 'ambre' | 'rouge' | 'insuffisante' | 'neutre';
  marque: string;
  titre: string;
  detail: string;
  lien: string;
}

/** Points à traiter dans le référentiel, calculés à partir des comptes réels du périmètre. */
export function usePointsReferentiel(situation: Situation | undefined): PointAttention[] {
  const { etat } = useSession();
  const lisible = situation?.installationsLisibles === true;
  const doublons = useDoublons(lisible);
  if (!situation || !lisible) return [];
  const administrateur = aUnRole(etat, 'administrateur_national');
  const createur = aUnRole(etat, 'administrateur_national', 'agent_administratif', 'distributeur');
  const compter = (e: string) => situation.comptes.filter((c) => c.etat === e).reduce((n, c) => n + c.nombre, 0);
  const points: PointAttention[] = [];
  const attente = compter('en_attente_validation');
  if (attente > 0)
    points.push({
      cle: 'attente',
      ton: 'ambre',
      marque: 'Validation, en attente',
      titre: `${pluriel(attente, 'installation')} en attente de validation`,
      detail: administrateur ? 'À examiner puis valider ou rejeter avec motif' : 'Validation par l’administrateur national',
      lien: '/referentiel/installations?etat=en_attente_validation',
    });
  const paires = doublons.data?.donnees.length ?? 0;
  if (paires > 0)
    points.push({
      cle: 'doublons',
      ton: 'neutre',
      marque: 'Référentiel, doublons possibles',
      titre: `${pluriel(paires, 'paire')} d’installations à arbitrer`,
      detail: administrateur ? 'Nom proche et faible distance : fusion à décider' : 'Arbitrage par l’administrateur national, consultation seule',
      lien: '/referentiel/doublons',
    });
  const brouillons = compter('brouillon');
  if (brouillons > 0 && createur)
    points.push({
      cle: 'brouillons',
      ton: 'neutre',
      marque: 'Référentiel, brouillons',
      titre: `${pluriel(brouillons, 'installation')} à l’état brouillon`,
      detail: 'À compléter puis soumettre à validation',
      lien: '/referentiel/installations?etat=brouillon',
    });
  return points;
}

export function ListePointsAttention({ points }: { points: PointAttention[] }) {
  return (
    <ol className="liste-exceptions">
      {points.map((p, i) => (
        <li key={p.cle}>
          <Link className={`exception ${p.ton}`} to={p.lien}>
            <span className="numero" aria-hidden="true">
              {i + 1}
            </span>
            <span className="texte">
              <span className="marque-exception">{p.marque}</span>
              <b>{p.titre}</b>
              <small>{p.detail}</small>
            </span>
            <Icone nom="chevron" />
          </Link>
        </li>
      ))}
    </ol>
  );
}

/** Qualité de l'information : hachurée tant qu'aucune déclaration n'est reçue ; ce n'est jamais une gravité. */
/**
 * Qualité de l'information par province (suffisance calculée par le serveur à partir des déclarations) : le
 * normal reste discret, seules les provinces à information insuffisante sont détaillées, avec leur motif. Avec
 * une province choisie, seule celle-ci est présentée.
 */
export function QualiteInformation({ situation, province = null }: { situation: Situation; province?: string | null }) {
  const provinces = situation.provinces.filter((p) => !province || p.code === province);
  const premiere = provinces[0];
  if (!premiere) return null;
  const seuil = premiere.seuilInformationSuffisante;
  const insuffisantes = provinces.filter((p) => p.information === 'insuffisante');
  if (insuffisantes.length === 0) {
    const parts = provinces.map((p) => p.partInstallationsRecentes).filter((x): x is number => x !== null);
    const minimum = parts.length ? Math.min(...parts) : null;
    return (
      <p className="attenue">
        Qualité de l’information : suffisante {provinces.length > 1 ? `dans les ${provinces.length} provinces` : `en ${premiere.nom}`}
        {minimum !== null ? `, au moins ${formaterNombre(String(minimum), 1)} % d’installations à donnée récente` : ''} (seuil {seuil} %).
      </p>
    );
  }
  const minuscule = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
  return (
    <div className="bloc-insuffisant">
      <b>
        Qualité de l’information : insuffisante{provinces.length > 1 ? ` dans ${insuffisantes.length} province${insuffisantes.length > 1 ? 's' : ''} sur ${provinces.length}` : ` en ${premiere.nom}`}
      </b>
      {insuffisantes.slice(0, 4).map((p) => (
        <span key={p.code}>
          {provinces.length > 1 ? `${p.nom} : ` : ''}
          {minuscule(p.motifInformation)}
        </span>
      ))}
      {insuffisantes.length > 4 && <span>Et {insuffisantes.length - 4} autre{insuffisantes.length - 4 > 1 ? 's' : ''}, hachurées sur la carte.</span>}
      <span>Ce n’est pas une gravité. Seuil de suffisance : {seuil} % d’installations à donnée récente.</span>
    </div>
  );
}

/** Comptes du référentiel d'un périmètre, par état et par catégorie. */
export function comptesReferentiel(situation: Situation, province: string | null) {
  const comptes = situation.comptes.filter((c) => !province || c.provinceCode === province);
  const somme = (f: (c: Situation['comptes'][number]) => boolean) => comptes.filter(f).reduce((n, c) => n + c.nombre, 0);
  return {
    comptes,
    total: somme(() => true),
    valides: somme((c) => c.etat === 'valide'),
    enAttente: somme((c) => c.etat === 'en_attente_validation'),
    stations: somme((c) => c.categorie === 'station'),
    depots: somme((c) => c.categorie === 'depot'),
    depotsEtCentres: somme((c) => ['depot', 'centre_emplissage', 'centre_chargement', 'raffinerie'].includes(c.categorie)),
    parCategorie: (categorie: string) => somme((c) => c.categorie === categorie),
  };
}

/**
 * Points d'attention tirés des alertes ouvertes (moteur d'alertes du lot 2), filtrées par le serveur dans le
 * périmètre du lecteur : les plus graves en tête, avec le nombre d'alertes ouvertes par niveau. Sans droit de
 * lecture des alertes, le bloc est « indisponible » avec son motif.
 */
export function PointsAlertes({ province = null, installationId = null, limite = 5 }: { province?: string | null; installationId?: string | null; limite?: number }) {
  const { etat } = useSession();
  const filtres = new URLSearchParams({ limite: String(limite) });
  if (province) filtres.set('province', province);
  if (installationId) filtres.set('installationId', installationId);
  const alertes = useQuery({
    queryKey: ['alertes', 'accueil', filtres.toString()],
    queryFn: async () => api<AlerteResume[]>(`/api/v1/alertes?${filtres.toString()}`),
    retry: false,
  });
  if (alertes.isLoading) return <Squelette lignes={3} libelle="Chargement des alertes ouvertes" />;
  if (alertes.error) {
    const refus = alertes.error instanceof ErreurApi && alertes.error.statut === 403;
    return <Indisponible motif={refus ? 'vos habilitations ne donnent pas accès aux alertes' : messageErreur(alertes.error)} />;
  }
  const liste = alertes.data!.donnees;
  const parNiveau = (alertes.data!.meta?.ouvertesParNiveau ?? {}) as Partial<Record<string, number>>;
  const total = Object.values(parNiveau).reduce<number>((n, x) => n + (x ?? 0), 0);
  const graves = (parNiveau.critique ?? 0) + (parNiveau.elevee ?? 0);
  const lienListe = etat?.navigation?.some((e) => e.code === 'alertes');
  const filtrees = Boolean(province || installationId);
  return (
    <>
      {!filtrees && total > 0 && (
        <p className="attenue">
          {pluriel(total, 'alerte ouverte', 'alertes ouvertes')} dans votre périmètre{graves > 0 ? `, dont ${formaterNombre(graves)} élevée${graves > 1 ? 's' : ''} ou critique${graves > 1 ? 's' : ''}` : ''}.
        </p>
      )}
      <ListeAlertesCompacte alertes={liste} vide={filtrees ? 'Aucune alerte ouverte ici.' : 'Aucune alerte ouverte dans votre périmètre.'} />
      {lienListe && total > liste.length && (
        <p className="lien-suite">
          <Link to={province ? `/alertes?province=${province}` : '/alertes'}>
            Toutes les alertes <Icone nom="chevron" taille={14} />
          </Link>
        </p>
      )}
    </>
  );
}
